import { useEffect, useState } from "react";
import { Cookie, Settings2 } from "lucide-react";
import { toast } from "sonner";
import {
  acceptAll,
  CONSENT_CHANGED_EVENT,
  getConsent,
  isGpcEnabled,
  openPreferences,
  rejectNonEssential,
} from "@/lib/consent";
import { CONSENT_T, getLang, type Lang } from "@/lib/i18n/consent";
import { cn } from "@/lib/utils";

/**
 * Inline cookie controls rendered in the footer sub-row.
 * Renders as a labeled group with two clearly-clickable pill buttons.
 */
export function CookieConsentChip() {
  const [lang, setLangState] = useState<Lang>("en");
  const [on, setOn] = useState<boolean>(() => getConsent()?.analytics === true);
  const gpc = typeof navigator !== "undefined" && isGpcEnabled();
  const t = CONSENT_T[lang];

  useEffect(() => {
    setLangState(getLang());
    const onLang = () => setLangState(getLang());
    const onConsent = () => setOn(getConsent()?.analytics === true);
    window.addEventListener("cws:lang-changed", onLang);
    window.addEventListener(CONSENT_CHANGED_EVENT, onConsent);
    onConsent();
    return () => {
      window.removeEventListener("cws:lang-changed", onLang);
      window.removeEventListener(CONSENT_CHANGED_EVENT, onConsent);
    };
  }, []);

  const toggle = () => {
    if (gpc) {
      toast(t.chipGpcLock);
      return;
    }
    if (on) {
      rejectNonEssential();
      toast(t.toastSavedNone);
    } else {
      acceptAll();
      toast.success(t.toastSavedAll);
    }
  };

  const pill =
    "inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] text-white/75 transition-all hover:-translate-y-px hover:border-white/40 hover:bg-white/10 hover:text-white";

  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-white/55">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-white/55">
        <Cookie aria-hidden className="h-3.5 w-3.5 text-accent/80" />
        {t.chipLabel}
      </span>

      <button
        type="button"
        onClick={toggle}
        aria-pressed={on}
        aria-label={`${t.chipLabel}: ${on ? t.chipOn : t.chipOff}`}
        disabled={gpc}
        className={cn(pill, gpc && "cursor-not-allowed opacity-60 hover:translate-y-0")}
      >
        <span
          aria-hidden
          className={cn(
            "inline-block h-1.5 w-1.5 rounded-full",
            on ? "bg-emerald-300" : "bg-white/30",
          )}
        />
        <span>{on ? t.chipOn : t.chipOff}</span>
        {gpc && <span className="ml-1 text-white/45">GPC</span>}
      </button>

      <button type="button" onClick={openPreferences} className={pill}>
        <Settings2 aria-hidden className="h-3 w-3" />
        <span>{t.chipCustomize}</span>
      </button>
    </span>
  );
}

export default CookieConsentChip;
